import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search, Pencil, Tag, X, Save, Loader2, PackageOpen, Camera, Image as ImageIcon } from "lucide-react";
import { compressImage } from "./StockForm";
import { Lightbox } from "./Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => "\u20b9" + Number(n || 0).toLocaleString("en-IN");
const FALLBACK_TYPES = ["Desktop", "Laptop", "UPS", "Monitor", "Printer", "Tablet", "Furniture", "Other"];

const inputCls = "w-full rounded-lg border px-3.5 py-2.5 text-[15px] outline-none focus:ring-4";
const inputStyle = { background: "#fff", borderColor: "#d8c8af", color: "#1F4A38" };

export const StockList = ({ headers }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sellingId, setSellingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState(FALLBACK_TYPES);
  const [editPhotos, setEditPhotos] = useState([]);
  const [lb, setLb] = useState(null);
  const camRef = useRef(null);
  const galRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/stock/options`).then((r) => r.json())
      .then((d) => Array.isArray(d.item_types) && d.item_types.length && setTypes(d.item_types))
      .catch(() => {});
  }, []);

  const openEdit = (p) => {
    setEditing({ ...p });
    setEditPhotos((Array.isArray(p.images) && p.images.length ? p.images : [p.img]).filter(Boolean));
  };

  const onEditPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const compressed = await Promise.all(files.slice(0, 6).map((f) => compressImage(f)));
      setEditPhotos((ph) => [...ph, ...compressed].slice(0, 6));
    } catch {
      alert("Could not read that photo. Please try another one.");
    }
    e.target.value = "";
  };

  const load = useCallback(() => {
    fetch(`${API}/products?include_inactive=true`)
      .then((r) => r.json())
      .then((d) => setItems(Array.isArray(d) ? d.slice().reverse() : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sell = async (p) => {
    if (!window.confirm(`Mark 1 unit of "${p.name}" as sold?`)) return;
    setSellingId(p.id);
    try {
      const r = await fetch(`${API}/stock/items/${p.id}/sell`, { method: "POST", headers });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      setItems((list) => list.map((it) => it.id === p.id ? { ...it, qty: data.qty, sold: data.sold } : it));
    } catch (err) {
      alert(err.message);
    } finally {
      setSellingId(null);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: editing.name, brand: editing.brand || "", code: editing.code || "",
                     qty: parseInt(editing.qty, 10) || 0, price: parseInt(editing.price, 10) || 0,
                     item_type: editing.type || "", photos: editPhotos };
      const r = await fetch(`${API}/stock/items/${editing.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Save failed");
      setItems((list) => list.map((it) => it.id === editing.id
        ? { ...it, ...body, type: editing.type, sold: data.sold, images: editPhotos,
            img: editPhotos[0] || it.img }
        : it));
      setEditing(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const term = q.trim().toLowerCase();
  const list = items.filter((p) => !term || `${p.name} ${p.brand} ${p.code} ${p.category}`.toLowerCase().includes(term));

  return (
    <div data-testid="stock-list">
      <div className="mb-3 flex items-center gap-2 rounded-full border bg-white px-4 py-2.5" style={{ borderColor: "var(--line)" }}>
        <Search size={16} style={{ color: "var(--muted)" }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, code, brand…"
               data-testid="stock-list-search" className="w-full bg-transparent text-[14.5px] outline-none" />
      </div>
      <p className="mb-3 text-[12.5px] font-semibold" style={{ color: "var(--muted)" }}>{list.length} items</p>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 size={28} className="animate-spin" style={{ color: "var(--green-800)" }} /></div>
      ) : list.length === 0 ? (
        <div className="grid place-items-center gap-2 rounded-2xl border py-14 text-center" style={{ background: "var(--cream-2)", borderColor: "var(--line)" }}>
          <PackageOpen size={34} style={{ color: "var(--muted)" }} />
          <p className="font-heading font-bold" style={{ color: "var(--green-950)" }}>No items found</p>
        </div>
      ) : (
        <div className="grid gap-2.5">
          {list.map((p) => (
            <article key={p.id} data-testid={`stock-list-item-${p.id}`} className="flex gap-3 rounded-xl border p-3"
                     style={{ background: "var(--cream-2)", borderColor: "var(--line)", opacity: p.sold ? 0.6 : 1 }}>
              <img src={(p.images && p.images[0]) || p.img} alt=""
                   onClick={() => setLb({ images: (p.images && p.images.length ? p.images : [p.img]).filter(Boolean), alt: p.name, url: `${window.location.origin}/product/${p.id}` })}
                   data-testid={`stock-photo-zoom-${p.id}`}
                   className="h-16 w-16 shrink-0 cursor-zoom-in rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="line-clamp-2 text-[13.5px] font-semibold leading-snug" style={{ color: "var(--green-950)" }}>{p.name}</p>
                  {p.sold && <span className="shrink-0 rounded px-1.5 py-0.5 text-[9.5px] font-extrabold text-white" style={{ background: "var(--orange-700)" }}>SOLD</span>}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px]" style={{ color: "var(--muted)" }}>
                  {p.code && <span className="font-heading font-bold" style={{ color: "#E8501A" }}>{p.code}</span>}
                  <span>Qty: <b style={{ color: "var(--green-900)" }}>{p.qty}</b></span>
                  <span>{p.price_on_request ? "On request" : inr(p.price)}</span>
                </div>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => openEdit(p)} data-testid={`stock-edit-${p.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-heading text-[12px] font-bold"
                          style={{ borderColor: "var(--line)", color: "var(--green-900)", background: "#fff" }}>
                    <Pencil size={12} /> Edit
                  </button>
                  {!p.sold && (
                    <button onClick={() => sell(p)} disabled={sellingId === p.id} data-testid={`stock-sell-${p.id}`}
                            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-heading text-[12px] font-bold text-white disabled:opacity-60"
                            style={{ background: "linear-gradient(135deg, var(--orange-700), var(--orange-500))" }}>
                      {sellingId === p.id ? <Loader2 size={12} className="animate-spin" /> : <Tag size={12} />} Sale
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-center" style={{ background: "rgba(31,74,56,0.5)" }} onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveEdit} data-testid="stock-edit-modal"
                className="w-full rounded-t-2xl p-5 sm:max-w-md sm:rounded-2xl" style={{ background: "var(--cream-2)", maxHeight: "88vh", overflowY: "auto" }}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-[17px] font-bold" style={{ color: "var(--green-950)" }}>Edit Item</h3>
              <button type="button" onClick={() => setEditing(null)} className="grid h-8 w-8 place-items-center rounded-full" style={{ background: "var(--green-100)" }}><X size={16} /></button>
            </div>
            <div className="grid gap-2.5">
              <label className="grid gap-1 font-heading text-[13px] font-semibold" style={{ color: "var(--green-950)" }}>
                Item Type
                <select value={editing.type || ""} onChange={(e) => setEditing({ ...editing, type: e.target.value })}
                        data-testid="stock-edit-type" className={inputCls} style={inputStyle}>
                  <option value="">Select type…</option>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                  {editing.type && !types.includes(editing.type) && <option value={editing.type}>{editing.type}</option>}
                </select>
              </label>
              <label className="grid gap-1 font-heading text-[13px] font-semibold" style={{ color: "var(--green-950)" }}>
                Name & Configuration
                <textarea value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required rows={2}
                          data-testid="stock-edit-name" className={`${inputCls} resize-none`} style={inputStyle} />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="grid gap-1 font-heading text-[13px] font-semibold" style={{ color: "var(--green-950)" }}>
                  Brand
                  <input value={editing.brand || ""} onChange={(e) => setEditing({ ...editing, brand: e.target.value })}
                         data-testid="stock-edit-brand" className={inputCls} style={inputStyle} />
                </label>
                <label className="grid gap-1 font-heading text-[13px] font-semibold" style={{ color: "var(--green-950)" }}>
                  Code
                  <input value={editing.code || ""} onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                         data-testid="stock-edit-code" className={`${inputCls} uppercase`} style={inputStyle} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="grid gap-1 font-heading text-[13px] font-semibold" style={{ color: "var(--green-950)" }}>
                  Quantity
                  <input type="number" min="0" value={editing.qty} onChange={(e) => setEditing({ ...editing, qty: e.target.value })}
                         inputMode="numeric" data-testid="stock-edit-qty" className={inputCls} style={inputStyle} />
                </label>
                <label className="grid gap-1 font-heading text-[13px] font-semibold" style={{ color: "var(--green-950)" }}>
                  Price (₹)
                  <input type="number" min="0" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                         inputMode="numeric" data-testid="stock-edit-price" className={inputCls} style={inputStyle} />
                </label>
              </div>
              <div>
                <span className="font-heading text-[13px] font-semibold" style={{ color: "var(--green-950)" }}>
                  Photos <span className="font-sans text-[11.5px] font-normal" style={{ color: "var(--muted)" }}>({editPhotos.length}/6)</span>
                </span>
                <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onEditPhoto}
                       className="hidden" data-testid="stock-edit-photo-input" />
                <input ref={galRef} type="file" accept="image/*" multiple onChange={onEditPhoto}
                       className="hidden" data-testid="stock-edit-gallery-input" />
                {editPhotos.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {editPhotos.map((p, i) => (
                      <div key={i} className="relative overflow-hidden rounded-lg border" style={{ borderColor: "#d8c8af" }}>
                        <img src={p} alt={`Photo ${i + 1}`} onClick={() => setLb({ images: editPhotos, start: i, alt: editing.name })}
                             data-testid={`stock-edit-photo-zoom-${i}`}
                             className="h-20 w-full cursor-zoom-in object-cover" />
                        {i === 0 && <span className="absolute left-1 top-1 rounded px-1.5 py-0.5 text-[9px] font-extrabold text-white" style={{ background: "var(--green-800)" }}>MAIN</span>}
                        <button type="button" onClick={() => setEditPhotos((ph) => ph.filter((_, j) => j !== i))}
                                data-testid={`stock-edit-photo-remove-${i}`}
                                className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full text-white"
                                style={{ background: "rgba(31,74,56,0.85)" }}><X size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}
                {editPhotos.length < 6 && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => camRef.current?.click()} data-testid="stock-edit-take-photo"
                            className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3.5 font-heading text-[12.5px] font-bold"
                            style={{ borderColor: "#d8c8af", color: "var(--green-800)", background: "rgba(255,255,255,0.55)" }}>
                      <Camera size={16} style={{ color: "#E8501A" }} /> Take Photo
                    </button>
                    <button type="button" onClick={() => galRef.current?.click()} data-testid="stock-edit-gallery"
                            className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3.5 font-heading text-[12.5px] font-bold"
                            style={{ borderColor: "#d8c8af", color: "var(--green-800)", background: "rgba(255,255,255,0.55)" }}>
                      <ImageIcon size={16} style={{ color: "#E8501A" }} /> Choose from Gallery
                    </button>
                  </div>
                )}
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="stock-edit-save"
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-full py-3 font-heading font-bold text-white disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes
            </button>
          </form>
        </div>
      )}
      {lb && <Lightbox images={lb.images} start={lb.start || 0} alt={lb.alt} shareUrl={lb.url} onClose={() => setLb(null)} />}
    </div>
  );
};
